import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import api from "../api";

const MESI = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno", "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];
const MESI_BREVI = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];
const EUR = (v) => Number(v || 0).toLocaleString("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 });

const LABELS = { fatto: "Completato", in_corso: "In lavorazione", scaduto: "Scaduto", da_fare: "Da fare", na: "Non previsto" };
const ICONS = { fatto: "✓", in_corso: "●", scaduto: "!", da_fare: "·", na: "–" };

const Cell = ({ cell, text, onClick, tid }) => {
  const s = cell?.state || "na";
  const title = [LABELS[s], cell?.note, cell?.by && `da ${cell.by}`,
    cell?.at && new Date(cell.at).toLocaleDateString("it-IT")].filter(Boolean).join(" · ");
  return (
    <td className={`cdo-cell cdo-${s}${onClick ? " cdo-click" : ""}`} onClick={onClick} title={title} data-testid={tid}>
      <span className="cdo-ico">{ICONS[s]}</span>
      {text ? <span className="cdo-txt">{text}</span> : null}
    </td>
  );
};

export default function ColpoDocchio() {
  const now = new Date();
  const [mode, setMode] = useState("mese");
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [what, setWhat] = useState("reg:fatture");
  const [regime, setRegime] = useState("");
  const [lateOnly, setLateOnly] = useState(false);
  const [q, setQ] = useState("");
  const [d, setD] = useState(null);
  const [yd, setYd] = useState(null);
  const [ad, setAd] = useState(null);
  const [busy, setBusy] = useState("");

  const loadMonth = () => api.get(`/overview/month?year=${year}&month=${month}`).then((r) => setD(r.data)).catch(() => setD(null));
  const loadYear = () => api.get(`/overview/year?year=${year}&what=${what}`).then((r) => setYd(r.data)).catch(() => setYd(null));
  const loadAnnual = () => api.get(`/overview/annual?year=${year}`).then((r) => setAd(r.data)).catch(() => setAd(null));
  useEffect(() => {
    if (mode === "mese") loadMonth(); else if (mode === "anno") loadYear(); else loadAnnual();
  }, [mode, year, month, what]); // eslint-disable-line

  const rows = useMemo(() => {
    const src = mode === "mese" ? (d?.clients || []) : mode === "anno" ? (yd?.clients || []) : (ad?.clients || []);
    return src.filter((c) => (!regime || c.regime === regime)
      && (!q || (c.name || "").toLowerCase().includes(q.toLowerCase()))
      && (!lateOnly || (mode === "mese"
        ? [...Object.values(c.reg || {}), c.f24, ...Object.values(c.fisc || {}), c.tasks].some((x) => x?.state === "scaduto")
        : mode === "annuali"
          ? [...Object.values(c.fisc || {}), c.redditi].some((x) => x?.state === "scaduto")
          : true)));
  }, [d, yd, ad, mode, regime, q, lateOnly]);

  // le spunte restano quelle delle pagine di origine: qui si chiamano gli stessi endpoint
  const toggleReg = async (c, key, cell) => {
    if (cell.state === "na" || busy) return;
    setBusy(`${c.id}:${key}`);
    try {
      const rp = d?.columns?.reg_period || { year, month };
      await api.post("/contabilita/check", { company_id: c.id, year: rp.year, period: `m${rp.month}`, task: key, done: cell.state !== "fatto" });
      await loadMonth();
    } finally { setBusy(""); }
  };
  const toggleF24 = async (c) => {
    if (!c.f24?.record_id || busy) return;
    setBusy(`${c.id}:f24`);
    try {
      await api.put(`/f24/records/${c.f24.record_id}`, { paid: c.f24.state !== "fatto" });
      await loadMonth();
    } finally { setBusy(""); }
  };
  const toggleFisc = async (c, key, cell) => {
    if (cell.state === "na" || busy) return;
    setBusy(`${c.id}:${key}`);
    try {
      await api.put("/scadenze/checklist", { key, company_id: c.id, field: "done", value: cell.state !== "fatto" });
      mode === "mese" ? await loadMonth() : await loadAnnual();
    } finally { setBusy(""); }
  };

  const t = d?.totals;
  const cols = d?.columns;

  return (
    <div className="cdo" data-testid="colpo-docchio">
      <div className="cdo-head">
        <div>
          <h1 style={{ margin: 0 }}>📋 Colpo d'occhio</h1>
          <p className="muted" style={{ margin: "4px 0 0" }}>Tutti i clienti e tutte le scadenze in una schermata. Clicca una cella per spuntarla: aggiorna la stessa spunta delle pagine di origine.</p>
        </div>
        <div className="cdo-switch">
          <button className={`btn sm${mode === "mese" ? " primary" : ""}`} onClick={() => setMode("mese")} data-testid="cdo-mode-month">Mese</button>
          <button className={`btn sm${mode === "anno" ? " primary" : ""}`} onClick={() => setMode("anno")} data-testid="cdo-mode-year">Anno</button>
          <button className={`btn sm${mode === "annuali" ? " primary" : ""}`} onClick={() => setMode("annuali")} data-testid="cdo-mode-annual">Annuali</button>
        </div>
      </div>

      <div className="cdo-tools">
        {mode === "mese" ? (
          <select value={month} onChange={(e) => setMonth(Number(e.target.value))} data-testid="cdo-month">
            {MESI.map((m, i) => <option key={i} value={i + 1}>{m}</option>)}
          </select>
        ) : mode === "annuali" ? null : (
          <select value={what} onChange={(e) => setWhat(e.target.value)} data-testid="cdo-what">
            <option value="reg:fatture">Registrazione fatture</option>
            <option value="reg:estratto_conto">Estratto conto</option>
            <option value="reg:paghe">Paghe</option>
            <option value="reg:prima_nota">Prima nota</option>
            <option value="reg:costi_no_iva">Costi no IVA</option>
            <option value="f24">F24 (IVA, ritenute, contributi)</option>
          </select>
        )}
        <select value={year} onChange={(e) => setYear(Number(e.target.value))} data-testid="cdo-year">
          {[now.getFullYear() - 1, now.getFullYear(), now.getFullYear() + 1].map((y) => <option key={y} value={y}>{y}</option>)}
        </select>
        <select value={regime} onChange={(e) => setRegime(e.target.value)} data-testid="cdo-regime">
          <option value="">Tutti i regimi</option>
          <option value="ordinario">Ordinario</option>
          <option value="semplificato">Semplificato</option>
          <option value="forfettario">Forfettario</option>
          <option value="solo_consulenza">Solo consulenza</option>
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="🔍 Cerca cliente…" data-testid="cdo-search" style={{ maxWidth: 220 }} />
        {(mode === "mese" || mode === "annuali") && (
          <label className="muted" style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={lateOnly} onChange={(e) => setLateOnly(e.target.checked)} data-testid="cdo-late-only" style={{ margin: 0 }} />
            Solo chi è in ritardo
          </label>
        )}
        <span className="cdo-legend">
          <i className="cdo-fatto" />Completato <i className="cdo-in_corso" />In lavorazione <i className="cdo-scaduto" />Scaduto <i className="cdo-na" />Non previsto
        </span>
      </div>

      {mode === "mese" && t && (
        <div className="cdo-kpis" data-testid="cdo-kpis">
          <div><b>{t.clients}</b> clienti</div>
          <div className="k-ok"><b>{t.complete}</b> completi</div>
          <div className="k-late"><b>{t.late}</b> in ritardo</div>
          <div className="k-pend"><b>{t.pending}</b> da chiudere</div>
          {t.nothing > 0 && <div className="muted"><b>{t.nothing}</b> senza adempimenti questo mese</div>}
          {cols?.f24 && <div className="muted">F24 {MESI[month - 1]}: scadenza {new Date(cols.f24.due_date).toLocaleDateString("it-IT")}</div>}
        </div>
      )}

      <div className="cdo-wrap">
        {mode === "mese" ? (
          !d ? <p className="muted">Carico…</p> : (
            <table className="cdo-table" data-testid="cdo-table-month">
              <thead>
                <tr>
                  <th className="cdo-sticky cdo-group">Cliente</th>
                  <th className="cdo-group" colSpan={cols.reg.length}>Registrazioni <span style={{ opacity: .75, fontWeight: 400, textTransform: "none" }}>di {cols.reg_period?.label}</span></th>
                  <th className="cdo-group">F24 / IVA</th>
                  {cols.fisc.length > 0 && <th className="cdo-group" colSpan={cols.fisc.length}>Scadenze fiscali</th>}
                  <th className="cdo-group">Incarichi</th>
                </tr>
                <tr>
                  <th className="cdo-sticky">{rows.length} clienti</th>
                  {cols.reg.map((c) => <th key={c.key}>{c.label}</th>)}
                  <th>{cols.f24 ? "Importo e stato" : "Nessuna scadenza"}</th>
                  {cols.fisc.map((f) => <th key={f.key} title={f.label}>{f.label.length > 22 ? `${f.label.slice(0, 20)}…` : f.label}<br /><span className="muted" style={{ fontWeight: 400 }}>{new Date(f.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}</span></th>)}
                  <th>Aperti</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`cdo-row-${c.id}`}>
                    <th className="cdo-sticky cdo-name">
                      <Link to={`/aziende/${c.id}`}>{c.name}</Link>
                      <span className="muted cdo-regime">{c.regime || "senza regime"}</span>
                    </th>
                    {cols.reg.map((col) => (
                      <Cell key={col.key} cell={c.reg[col.key]} onClick={c.reg[col.key].state !== "na" ? () => toggleReg(c, col.key, c.reg[col.key]) : null} tid={`cdo-reg-${c.id}-${col.key}`} />
                    ))}
                    <Cell cell={c.f24} text={c.f24.state !== "na" ? EUR(c.f24.amount) : ""} onClick={c.f24.record_id ? () => toggleF24(c) : null} tid={`cdo-f24-${c.id}`} />
                    {cols.fisc.map((f) => (
                      <Cell key={f.key} cell={c.fisc[f.key]} onClick={c.fisc[f.key].state !== "na" ? () => toggleFisc(c, f.key, c.fisc[f.key]) : null} tid={`cdo-fisc-${c.id}`} />
                    ))}
                    <td className={`cdo-cell cdo-${c.tasks.state}`} title={c.tasks.items.map((i) => `${i.title}${i.due_date ? ` (${new Date(i.due_date).toLocaleDateString("it-IT")})` : ""}`).join("\n")} data-testid={`cdo-tasks-${c.id}`}>
                      {c.tasks.open > 0
                        ? <Link to="/incarichi" className="cdo-txt">{c.tasks.open}{c.tasks.late > 0 ? ` · ${c.tasks.late} in ritardo` : ""}</Link>
                        : <span className="cdo-ico">–</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : mode === "annuali" ? (
          !ad ? <p className="muted">Carico…</p> : (
            <table className="cdo-table" data-testid="cdo-table-annual">
              <thead>
                <tr>
                  <th className="cdo-sticky">{rows.length} clienti · {ad.year}</th>
                  <th title="Stato preso dai fogli Redditi SC/SP/PF/730 dello Scadenzario fiscale">Dichiarazione<br /><span className="muted" style={{ fontWeight: 400 }}>redditi</span></th>
                  {ad.columns.map((c) => (
                    <th key={c.key} title={`${c.label} — scadenza ${new Date(c.date).toLocaleDateString("it-IT")}`}>
                      {c.label.length > 24 ? `${c.label.slice(0, 22)}…` : c.label}<br />
                      <span className="muted" style={{ fontWeight: 400 }}>{new Date(c.date).toLocaleDateString("it-IT", { day: "2-digit", month: "2-digit" })}</span>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`cdo-arow-${c.id}`}>
                    <th className="cdo-sticky cdo-name">
                      <Link to={`/aziende/${c.id}`}>{c.name}</Link>
                      <span className="muted cdo-regime">{c.categoria ? { sc: "società di capitali", sp: "società di persone", pf: "persona fisica", 730: "modello 730" }[c.categoria] : (c.regime || "senza regime")}</span>
                    </th>
                    <td className={`cdo-cell cdo-${c.redditi.state}`} title={[LABELS[c.redditi.state], c.redditi.note].filter(Boolean).join(" · ")} data-testid={`cdo-redditi-${c.id}`}>
                      <Link to="/scadenzario-fiscale" className="cdo-ico">{ICONS[c.redditi.state]}</Link>
                    </td>
                    {ad.columns.map((col) => (
                      <Cell key={col.key} cell={c.fisc[col.key]} onClick={c.fisc[col.key].state !== "na" ? () => toggleFisc(c, col.key, c.fisc[col.key]) : null} tid={`cdo-ann-${c.id}`} />
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        ) : (
          !yd ? <p className="muted">Carico…</p> : (
            <table className="cdo-table" data-testid="cdo-table-year">
              <thead>
                <tr>
                  <th className="cdo-sticky">{yd.label} · {yd.year}</th>
                  {MESI_BREVI.map((m) => <th key={m}>{m}</th>)}
                </tr>
              </thead>
              <tbody>
                {rows.map((c) => (
                  <tr key={c.id} data-testid={`cdo-yrow-${c.id}`}>
                    <th className="cdo-sticky cdo-name">
                      <Link to={`/aziende/${c.id}`}>{c.name}</Link>
                      <span className="muted cdo-regime">{c.regime || "senza regime"}</span>
                    </th>
                    {MESI_BREVI.map((_, i) => <Cell key={i} cell={c.months[i + 1] || c.months[String(i + 1)]} tid={`cdo-y-${c.id}-${i + 1}`} />)}
                  </tr>
                ))}
              </tbody>
            </table>
          )
        )}
      </div>
      <p className="muted" style={{ fontSize: 12 }}>
        Le spunte sono le stesse di <Link to="/contabilita">Situazione registrazioni</Link>, <Link to="/scadenze-f24">Scadenze F24</Link>, <Link to="/scadenzario-fiscale">Scadenzario fiscale</Link> e <Link to="/incarichi">Incarichi</Link>: qui le vedi e le aggiorni, là trovi i dettagli.
        Un adempimento mensile diventa «scaduto» quando il mese è finito; l'F24 quando passa la data di versamento.
      </p>
    </div>
  );
}
